import React, { useState } from 'react';
import ApiService from '../../services/api';

/**
 * AI SUMMARY:
 * The FundSourceModal component provides a modal interface for adding new fund sources.
 * Features:
 * - Form with validation for required fields
 * - Support for different source types (tradier, coinbase, etc.)
 * - Dynamic form fields based on selected source type
 * - Error handling and loading state management
 * - Callback for successful source addition
 */

interface FundSourceModalProps {
  fundId: string;
  isOpen: boolean;
  onClose: () => void;
  onSourceAdded: () => void;
}

const FundSourceModal: React.FC<FundSourceModalProps> = ({
  fundId,
  isOpen,
  onClose,
  onSourceAdded,
}) => {
  const [sourceType, setSourceType] = useState<string>('tradier');
  const [sourceId, setSourceId] = useState<string>('');
  const [apiKey, setApiKey] = useState<string>('');
  const [apiSecret, setApiSecret] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!sourceType || !sourceId) {
      setError('Source type and ID are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('apiToken');
      if (!token) {
        setError('No API token found');
        setLoading(false);
        return;
      }

      const apiService = new ApiService(token);

      // Prepare data object based on source type
      const sourceData: {
        sourceType: string;
        sourceId: string;
        data?: Record<string, any>;
      } = {
        sourceType,
        sourceId,
      };

      // Add additional data for specific source types
      if (sourceType === 'tradier' || sourceType === 'coinbase') {
        sourceData.data = {
          apiKey,
          apiSecret,
        };
      }

      await apiService.addFundSource(fundId, sourceData);

      // Reset form and close modal
      setSourceType('tradier');
      setSourceId('');
      setApiKey('');
      setApiSecret('');
      onSourceAdded();
      onClose();
    } catch (error) {
      console.error('Error adding fund source:', error);
      setError('Failed to add fund source');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Add Fund Source</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              className="block text-gray-700 text-sm font-bold mb-2"
              htmlFor="sourceType"
            >
              Source Type *
            </label>
            <select
              id="sourceType"
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required
            >
              <option value="tradier">Tradier</option>
              <option value="coinbase">Coinbase</option>
              <option value="manual">Manual</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="mb-4">
            <label
              className="block text-gray-700 text-sm font-bold mb-2"
              htmlFor="sourceId"
            >
              Source ID *
            </label>
            <input
              id="sourceId"
              type="text"
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="Enter source identifier"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {sourceType === 'tradier'
                ? 'Enter your Tradier account number'
                : sourceType === 'coinbase'
                  ? 'Enter your Coinbase account ID'
                  : 'Enter a unique identifier for this source'}
            </p>
          </div>

          {(sourceType === 'tradier' || sourceType === 'coinbase') && (
            <>
              <div className="mb-4">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="apiKey"
                >
                  API Key *
                </label>
                <input
                  id="apiKey"
                  type="text"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter API key"
                  required
                />
              </div>

              <div className="mb-6">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="apiSecret"
                >
                  API Secret *
                </label>
                <input
                  id="apiSecret"
                  type="password"
                  value={apiSecret}
                  onChange={(e) => setApiSecret(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter API secret"
                  required
                />
              </div>
            </>
          )}

          <div className="flex items-center justify-end">
            <button
              type="button"
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded mr-2"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              disabled={loading}
            >
              {loading ? 'Adding...' : 'Add Source'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FundSourceModal;
